// First-login onboarding. PROVISIONAL.
//
// The backend has not defined GET and PATCH /brands/:id/onboarding yet.
// This reader follows the spec's OnboardingState: step, per-integration
// status (connected, missing, expiring), and the planning step. User ids
// are not kept. PATCH records the next step only. Connecting still happens
// in each integration's own flow, on the existing Connect page.
//
// This app calls /api/brands/:brandId/onboarding
// The walkthrough renders only when VITE_PLAN_ONBOARDING=true.

import { apiJson } from '../auth/apiFetch';
import { isMissingEndpoint, safeText } from './client';

/** Provisional onboarding step. */
export type OnboardingStep = 'integrations' | 'readiness' | 'plan' | 'approve' | 'done';

/** Provisional integration row on the checklist. */
export type OnboardingIntegration = {
  provider: string;
  status: 'Connected' | 'Missing' | 'Expiring';
  path: 'Direct' | 'Supermetrics' | null;
};

/** Provisional readiness checks. Null means the backend has not checked yet. */
export type OnboardingReadiness = {
  catalogHasProducts: boolean | null;
  colorwaysInStock: boolean | null;
  metaReadable: boolean | null;
  note: string | null;
};

/** Provisional onboarding view. */
export type PlanOnboarding = {
  step: OnboardingStep;
  integrations: OnboardingIntegration[];
  readiness: OnboardingReadiness;
  note: string | null;
};

type Rec = Record<string, unknown>;

const PROVIDERS: Record<string, string> = {
  meta: 'Meta Ads and catalog',
  'meta ads': 'Meta Ads and catalog',
  shopify: 'Shopify',
  'google ads': 'Google Ads',
  google: 'Google Ads',
  ga4: 'GA4',
  klaviyo: 'Klaviyo'
};

export function planOnboardingEnabled(env: { VITE_PLAN_ONBOARDING?: string } = import.meta.env): boolean {
  return env.VITE_PLAN_ONBOARDING === 'true';
}

export function onboardingUrl(brandId: string): string {
  return `/api/brands/${encodeURIComponent(brandId)}/onboarding`;
}

export function stepLabel(step: OnboardingStep): string {
  if (step === 'integrations') return 'Connect integrations';
  if (step === 'readiness') return 'Check readiness';
  if (step === 'plan') return 'Draft a plan';
  if (step === 'approve') return 'Approve the plan';
  return 'Setup complete';
}

export async function getPlanOnboarding(brandId: string): Promise<PlanOnboarding | null> {
  try {
    const body = await apiJson<unknown>(onboardingUrl(brandId), { cache: 'no-store' });
    return readPlanOnboarding(body);
  } catch (err: unknown) {
    if (isMissingEndpoint(err)) return null;
    return null;
  }
}

export async function advancePlanOnboarding(brandId: string, step: OnboardingStep): Promise<PlanOnboarding | null> {
  try {
    const body = await apiJson<unknown>(onboardingUrl(brandId), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step })
    });
    return readPlanOnboarding(body) ?? { step, integrations: [], readiness: emptyReadiness(), note: null };
  } catch {
    return null;
  }
}

export function readPlanOnboarding(body: unknown): PlanOnboarding | null {
  const payload = payloadOf(body);
  if (!payload) return null;
  const step = readStep(payload.step ?? payload.currentStep);
  const completed = payload.completed === true || payload.completedAt != null;
  if (!step && !completed) return null;
  return {
    step: completed ? 'done' : (step as OnboardingStep),
    integrations: records(payload.integrations ?? payload.sources).map(readIntegration).filter((row): row is OnboardingIntegration => row != null),
    readiness: readReadiness(payload.readiness),
    note: prose(payload.note ?? payload.summary, 240)
  };
}

function readIntegration(row: Rec): OnboardingIntegration | null {
  const provider = providerLabel(row.provider ?? row.source ?? row.name);
  const status = statusLabel(row.status ?? row.state);
  if (!provider || !status) return null;
  return { provider, status, path: pathLabel(row.ingestPath ?? row.path) };
}

function readReadiness(value: unknown): OnboardingReadiness {
  if (!isRec(value)) return emptyReadiness();
  return {
    catalogHasProducts: flag(value.catalogHasProducts ?? value.hasProducts ?? value.catalog),
    colorwaysInStock: flag(value.colorwaysInStock ?? value.inStockColorways ?? value.stock),
    metaReadable: flag(value.metaReadable ?? value.metaCanBeRead ?? value.meta),
    note: prose(value.note ?? value.summary, 240)
  };
}

function emptyReadiness(): OnboardingReadiness {
  return { catalogHasProducts: null, colorwaysInStock: null, metaReadable: null, note: null };
}

function readStep(value: unknown): OnboardingStep | null {
  const raw = prose(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'integrations' || key === 'connect' || key === 'integration') return 'integrations';
  if (key === 'readiness' || key === 'check readiness' || key === 'ready') return 'readiness';
  if (key === 'plan' || key === 'planning') return 'plan';
  if (key === 'approve' || key === 'approval') return 'approve';
  if (key === 'done' || key === 'complete' || key === 'completed') return 'done';
  return null;
}

function providerLabel(value: unknown): string | null {
  const raw = prose(value, 60);
  if (!raw) return null;
  return PROVIDERS[raw.toLowerCase().replace(/[_-]+/g, ' ')] ?? raw;
}

function statusLabel(value: unknown): OnboardingIntegration['status'] | null {
  const raw = prose(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'connected' || key === 'healthy' || key === 'ok') return 'Connected';
  if (key === 'missing' || key === 'disconnected') return 'Missing';
  if (key === 'expiring' || key === 'expired') return 'Expiring';
  return null;
}

function pathLabel(value: unknown): 'Direct' | 'Supermetrics' | null {
  const raw = prose(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'direct') return 'Direct';
  if (key === 'supermetrics') return 'Supermetrics';
  return null;
}

function flag(value: unknown): boolean | null {
  if (value === true || value === false) return value;
  return null;
}

function payloadOf(body: unknown): Rec | null {
  if (!isRec(body)) return null;
  const nested = isRec(body.onboarding) ? body.onboarding : null;
  if (!nested) return body;
  return body.step != null || body.completed != null ? body : nested;
}

function prose(value: unknown, limit: number): string | null {
  const text = safeText(value, limit);
  if (!text || /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/.test(text)) return null;
  return text;
}

function isRec(value: unknown): value is Rec {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function records(value: unknown): Rec[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRec);
}
